import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import i18n from "i18next";

/** Currency of a monetary value whose currency is unset: the platform defaults to EUR. */
export const DEFAULT_CURRENCY: Currencies = "EUR";

/** The ISO 4217 code shown for a currency, {@link DEFAULT_CURRENCY} when it is unset. */
export function currencyCode(currency: Currencies | null | undefined): Currencies {
	return currency ?? DEFAULT_CURRENCY;
}

/** Decimal places of a currency's minor unit (EUR → 2, JPY → 0), 2 when the runtime cannot resolve it. */
export function currencyMinorUnits(currency: Currencies | null | undefined): number {
	try {
		return (
			new Intl.NumberFormat("en", { style: "currency", currency: currencyCode(currency) }).resolvedOptions()
				.maximumFractionDigits ?? 2
		);
	} catch {
		return 2;
	}
}

/**
 * Renders a monetary amount in the user's locale with the ISO code, e.g. `500000` EUR → `"EUR 500,000.00"`,
 * `-12.5` USD → `"USD -12.50"` (Intl separates code and number with a no-break space). The minus sits
 * directly before the first digit, whichever side of the number the locale puts the code on, and an
 * amount that rounds to zero has none. Decimals default to the currency's minor units. A nullish amount
 * renders as `fallback`.
 */
export function formatCurrencyAmount(
	amount: number | null | undefined,
	currency: Currencies | null | undefined,
	decimalPlaces?: number,
	fallback = "-",
): string {
	if (amount == null) {
		return fallback;
	}
	const digits = decimalPlaces ?? currencyMinorUnits(currency);
	const unsigned = new Intl.NumberFormat(i18n.language, {
		style: "currency",
		currency: currencyCode(currency),
		currencyDisplay: "code",
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	}).format(Math.abs(amount));
	const isVisiblyNegative = amount < 0 && /[1-9]/.test(unsigned);
	return isVisiblyNegative ? unsigned.replace(/\d/, "-$&") : unsigned;
}
